import type {
  GatewayDetailDto,
  HostDto,
} from '@terra/shared-contracts-external-network';
import { Badge, DrawerCustomSection, Text } from '@terra/ui-design-system';
import { HStack, VStack } from '@terra/ui-styled-system';
import { TechnologyIcon } from '@terra/ui-design-system';
import type { DrawerDetailRow } from '@terra/ui-design-system';
import { DrawerGeneralDetails } from '@terra/ui-design-system';
import type { ReactNode } from 'react';

type AssetDetailTranslator = (key: string) => string;

function humanizeSnakeCase(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function ServedNamesSection({
  names,
  title,
  sourceLabel,
}: {
  names: Array<{ domainId: string; fqdn: string; source?: string }>;
  title: string;
  sourceLabel: string;
}) {
  if (names.length === 0) {
    return null;
  }

  return (
    <DrawerCustomSection title={title}>
      <VStack gap={2} alignItems="stretch" w="full">
        {names.map((name) => (
          <HStack key={name.domainId} gap={2} justifyContent="space-between">
            <Text fontSize="sm">{name.fqdn}</Text>
            {name.source ? (
              <Badge variant="outline" size="sm" colorPalette="gray">
                {sourceLabel}: {humanizeSnakeCase(name.source)}
              </Badge>
            ) : null}
          </HStack>
        ))}
      </VStack>
    </DrawerCustomSection>
  );
}

export function buildHostOverviewContent({
  host,
  typeBadge,
  seenRows,
  t,
}: {
  host: HostDto;
  typeBadge: ReactNode;
  seenRows: DrawerDetailRow[];
  t: AssetDetailTranslator;
}) {
  return (
    <VStack gap={8} alignItems="stretch">
      <DrawerGeneralDetails
        title={t('generalDetails')}
        rows={[
          [
            { title: t('ipAddress'), value: host.ipAddress },
            { title: t('type'), value: typeBadge },
          ],
          ...(host.cloudProvider
            ? ([
                [
                  {
                    title: t('cloudProvider'),
                    value: (
                      <HStack gap={2} alignItems="center">
                        <TechnologyIcon
                          name={host.cloudProvider}
                          size={16}
                          showInitials
                        />
                        <Text fontSize="sm">{host.cloudProvider}</Text>
                      </HStack>
                    ),
                  },
                ],
              ] satisfies DrawerDetailRow[][])
            : []),
          seenRows,
        ]}
      />
      {host.hostnames.length > 0 ? (
        <DrawerCustomSection title={t('hostnames')}>
          <HStack gap={2} flexWrap="wrap" w="full">
            {host.hostnames.map((hostname) => (
              <Badge
                key={hostname}
                variant="outline"
                size="sm"
                colorPalette="cyan"
              >
                {hostname}
              </Badge>
            ))}
          </HStack>
        </DrawerCustomSection>
      ) : null}
      <ServedNamesSection
        names={host.servedNames ?? []}
        title={t('domain')}
        sourceLabel={t('source')}
      />
    </VStack>
  );
}

export function buildGatewayOverviewContent({
  gateway,
  typeBadge,
  seenRows,
  t,
}: {
  gateway: GatewayDetailDto;
  typeBadge: ReactNode;
  seenRows: DrawerDetailRow[];
  t: AssetDetailTranslator;
}) {
  return (
    <VStack gap={8} alignItems="stretch">
      <DrawerGeneralDetails
        title={t('generalDetails')}
        rows={[
          [
            {
              title: t('hostname'),
              value: gateway.hostname,
              withTooltip: true,
            },
            { title: t('type'), value: typeBadge },
          ],
          [
            {
              title: t('provider'),
              value: gateway.provider ?? '—',
            },
            {
              title: t('category'),
              value: humanizeSnakeCase(gateway.providerCategory),
            },
          ],
          seenRows,
        ]}
      />
      {gateway.ips.length > 0 ? (
        <DrawerCustomSection title={t('ipAddresses')}>
          <HStack gap={2} flexWrap="wrap" w="full">
            {gateway.ips.map((ip) => (
              <Badge key={ip} variant="outline" size="sm">
                {ip}
              </Badge>
            ))}
          </HStack>
        </DrawerCustomSection>
      ) : null}
      <ServedNamesSection
        names={gateway.domains.map((domain) => ({
          domainId: domain.id,
          fqdn: domain.fqdn,
          source: domain.source,
        }))}
        title={t('domain')}
        sourceLabel={t('source')}
      />
    </VStack>
  );
}
